import {
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  catchError,
  filter,
  Observable,
  of,
  ReplaySubject,
  Subject,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { AuthConfigToken } from '../ng-security.module';
import { WebappService } from '../providers/webapp/webapp.service';
import { PlatformService } from '../service';

const DEFAULT_WHITELIST = [
  '^https://test.edu-api.thiememeulenhoff.nl',
  '^https://test.thor.thiememeulenhoff.nl',
  '^https://test.thip.thiememeulenhoff.nl',
  '^https://test.groupservices.thiememeulenhoff.nl',
  '^https://test.user-service.thiememeulenhoff.nl',
  '^https://test.edition.thiememeulenhoff.nl',
  '^https://acc.edu-api.thiememeulenhoff.nl',
  '^https://acc.thor.thiememeulenhoff.nl',
  '^https://acc.thip.thiememeulenhoff.nl',
  '^https://acc.groupservices.thiememeulenhoff.nl',
  '^https://acc.edition.thiememeulenhoff.nl',
  '^https://stag.edu-api.thiememeulenhoff.nl',
  '^https://stag.thor.thiememeulenhoff.nl',
  '^https://stag.thip.thiememeulenhoff.nl',
  '^https://stag.groupservices.thiememeulenhoff.nl',
  '^https://stag.edition.thiememeulenhoff.nl',
  '^https://edu-api.thiememeulenhoff.nl',
  '^https://thor.thiememeulenhoff.nl',
  '^https://thip.thiememeulenhoff.nl',
  '^https://groupservices.thiememeulenhoff.nl',
  '^https://user-service.thiememeulenhoff.nl',
  '^https://edition.thiememeulenhoff.nl',
  '^https:\\/\\/test\\.platforms\\.thiememeulenhoff\\.nl((?!\\/api\\/v1\\/auth\\/token).+)$',
  '^https:\\/\\/platforms\\.thiememeulenhoff\\.nl((?!\\/api\\/v1\\/auth\\/token).+)$',
  '^https?://thor.localhost',
  '^https?://thip.localhost',
  '^https?://vesta.localhost',
  '^https?://dev.edition/secure',
  '^https://arrange.localhost',
  '^https://test.vesta.thiememeulenhoff.nl',
  '^https://acc.vesta.thiememeulenhoff.nl',
  '^https://stag.vesta.thiememeulenhoff.nl',
  '^https://vesta.thiememeulenhoff.nl',
  '^https://test.think.thiememeulenhoff.nl',
  '^https://acc.think.thiememeulenhoff.nl',
  '^https://stag.think.thiememeulenhoff.nl',
  '^https://think.thiememeulenhoff.nl',
];

@Service({ autoProvided: false })
export class PlatformHttpInterceptor implements HttpInterceptor {
  readonly #platform = inject(PlatformService);

  readonly #activatedRoute = inject(ActivatedRoute);

  readonly #router = inject(Router);

  readonly #webappService = inject(WebappService);

  readonly #authConfig = inject(AuthConfigToken);

  readonly #urlWhitelist =
    this.#authConfig.interceptorUrlWhitelist ?? DEFAULT_WHITELIST;

  private static loginSubject?: Subject<unknown>;

  intercept(req: HttpRequest<never>, next: HttpHandler) {
    if (this.#passWhitelistTest(req.url)) {
      return this.#handleUrl(req, next);
    }

    return next.handle(req);
  }

  #passWhitelistTest(url: string) {
    return this.#urlWhitelist.some((expression) => {
      return new RegExp(expression).test(url);
    });
  }

  #handleUrl(req: HttpRequest<never>, next: HttpHandler) {
    return this.#performLoginIfNeeded().pipe(
      switchMap(() => this.#performNextRequest(req, next)),
    );
  }

  #performLoginIfNeeded() {
    if (this.#platform.isLoggedIn) {
      return of(null);
    }

    return this.#tryPerformLogin();
  }

  #performNextRequest(req: HttpRequest<never>, next: HttpHandler) {
    return next
      .handle(this.#cloneRequest(req))
      .pipe(catchError((error) => this.#catchError(error, req, next)));
  }

  #cloneRequest(req: HttpRequest<never>) {
    const { accessToken } = this.#platform;
    const headers = req.headers.set('Authorization', `Bearer ${accessToken}`);
    return req.clone({ headers });
  }

  #catchError(
    error: HttpErrorResponse,
    req: HttpRequest<never>,
    next: HttpHandler,
  ) {
    if (error.status === 401) {
      return this.#tryPerformLogin().pipe(
        switchMap(() => next.handle(this.#cloneRequest(req))),
      );
    }

    return throwError(() => error);
  }

  #tryPerformLogin() {
    if (PlatformHttpInterceptor.loginSubject) {
      return PlatformHttpInterceptor.loginSubject.asObservable();
    }

    PlatformHttpInterceptor.loginSubject = new ReplaySubject();
    const obs = PlatformHttpInterceptor.loginSubject.asObservable();

    this.#performLogin()
      .pipe(
        tap(() => {
          PlatformHttpInterceptor.loginSubject = undefined;
        }),
      )
      .subscribe(PlatformHttpInterceptor.loginSubject);

    return obs;
  }

  #performLogin() {
    if (this.#activatedRoute.component !== null) {
      return this.#performPlatformOrWebappLogin();
    }

    return new Observable((observer) => {
      this.#router.events
        .pipe(
          filter(() => this.#activatedRoute.snapshot.firstChild !== null),
          take(1),
          switchMap(() => this.#performPlatformOrWebappLogin()),
        )
        .subscribe(observer);
    });
  }

  #performPlatformOrWebappLogin() {
    const useWebappPlatformCall =
      this.#authConfig.useWebappPlatformCall === true;

    if (useWebappPlatformCall) {
      return this.#webappService.login() as Observable<unknown>;
    }

    return this.#platform.login() as Observable<unknown>;
  }
}
